import React from "react";
import { Text, BlockStack, Box, InlineStack } from "@shopify/polaris";
import { getWithExpiry } from "../../utils/LocalStorageExpiry";
import "./AppRatingWidget.css";
import DismissableBanner, { setPropnameDismissed } from "../Banners/DismissableBanner";
import { useMutation } from "@apollo/client";
import { SET_FEEDBACK } from "../../graphql/Mutations/Settings";

/**
 * Standalone star rating widget with localStorage-based dismiss and expiry.
 * - Shows 5 interactive stars
 * - Sends POST /api/rate/{rating} by default
 * - If rating is 5 and onRequestReview is provided, calls it
 * - Hides itself after rating or dismiss, and won't reappear until expiry
 */
const AppRatingWidget = ({
  hideKey = "ratingCard",
  hideDays = 60,
  onRate,
  onRequestReview,
  showToast,
  paddingStart = 0, 
  paddingEnd = 0,
  direction = "column",
  enableShake = true,
  showGradient = true
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [selectedRating, setSelectedRating] = React.useState(0);
  const [hoveredRating, setHoveredRating] = React.useState(0);
  const [shake, setShake] = React.useState(false);
  const [setFeedback, { loading }] = useMutation(SET_FEEDBACK(), {
    onError: (error) => {
      console.error("Failed to submit rating:", error);
    }
  });

  React.useEffect(() => {
    const hidden = getWithExpiry(`dismissed_${hideKey}`);
    if (hidden === "true") {
      setIsVisible(false);
      return;
    }
    setIsVisible(true);
    if (!enableShake) return;
    const startShakeTimer = setTimeout(() => {
      setShake(true);
      const stopShakeTimer = setTimeout(() => {
        setShake(false);
      }, 1000);
      return () => clearTimeout(stopShakeTimer);
    }, 2000);
    return () => clearTimeout(startShakeTimer);
  }, [hideKey, enableShake]);

  const sendRating = async (rating) => {
    if (typeof onRate === "function") {
      await onRate(rating);
      return;
    }
    await setFeedback({ variables: { feedback: String(rating) } });
  };

  const handleClickStar = async (rating) => {
    if (loading) {
      return;
    }
    if (rating === 5 && typeof onRequestReview === "function") {
      try {
        await onRequestReview();
      } catch (err) {
        // ignore review request errors
      }
    }
    setPropnameDismissed(hideKey, hideDays * 24);
    setSelectedRating(rating);
    setIsVisible(false);
    try {
      if (rating < 5 && typeof showToast === "function") {
        showToast("Thank you for the rating!", { duration: 1500 });
      }
      await sendRating(rating);
    } finally {
      setIsVisible(false);
    }
  };

  if (!isVisible) return null;
  
  const WrapperElement = direction === "column" 
    ? ({children}) =>
        <BlockStack gap={200}>
          {children}
        </BlockStack>
    : ({children}) =>
      <InlineStack gap={200} align="center" blockAlign="center">
        {React.Children.toArray(children).map((child, index, array) => (
          <React.Fragment key={index}>
            {child}
            {index < array.length - 1 && <span>–</span>}
          </React.Fragment>
        ))}
      </InlineStack>

  return (
    <div className={`rating-card-container${showGradient ? " rating-banner-gradient" : ""}${shake ? " shake" : ""}`}>
      <DismissableBanner propName={hideKey} expiryHrs={24 * hideDays} paddingStart={paddingStart} paddingEnd={paddingEnd} hideIcon>
        <Box padding={100}>
          <WrapperElement>
            {(() => {
              const now = new Date();
              const isBfcmPeriod = now.getMonth() === 11 && now.getDate() <= 14; // Dec 1-14
              return isBfcmPeriod ? (
                <>
                  <Text as="h2" variant="headingSm">Thanks for choosing Disco this BFCM! <span role="img" aria-label="black-heart">🖤</span></Text>
                  <Text as="p" variant="bodyMd">Rate your experience – it means the world to us <span role="img" aria-label="holding-back-tears">🥹</span></Text>
                </>
              ) : (
                <>
                  <Text as="h2" variant="headingSm">Enjoying the app? Give us a rating <span role="img" aria-label="heart">❤️</span></Text>
                  <Text as="p" variant="bodyMd">Ratings mean a lot to us and help us grow <span role="img" aria-label="holding-back-tears">🥹</span></Text>
                </>
              );
            })()}
            <div className="stars">
              {[1, 2, 3, 4, 5].map((value) => {
                const activeThreshold = hoveredRating || selectedRating;
                return (
                  <div
                    key={value}
                    id={`star${value}`}
                    className={(hoveredRating === 0 && selectedRating === 0) ? "star star-breathing" : "star"}
                    style={{
                      color: value <= activeThreshold ? "#FFD700" : "gray",
                      textShadow: value <= activeThreshold
                        ? "0 0 8px rgba(255, 215, 0, 0.6), 0 0 12px rgba(255, 223, 0, 0.4)"
                        : "none",
                      transform: value <= hoveredRating ? "scale(1.15)" : "scale(1)",
                      transition: "all 0.3s ease"
                    }}
                    onClick={() => handleClickStar(value)}
                    onMouseEnter={() => setHoveredRating(value)}
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    ★
                  </div>
                );
              })}
            </div>
          </WrapperElement>
        </Box>
       
      </DismissableBanner>
    </div>
  );
};

export default AppRatingWidget;



